import { useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useT } from "@/i18n";
import { useSiteContent } from "@/context/SiteContentContext";
import { sectionScheme, schemeHex } from "@/lib/schemes";
import { pageSchemeFor } from "@/lib/pageScheme";

/**
 * Minimal header — no logo (removed per brief).
 * EN/ES as visible pill buttons; default from browser locale on first mount.
 * Nav is quiet, no borders, no oversized Shop CTA.
 */
const CORE_LINKS = [
    { to: "/artists", key: "artists", i18nFallback: "nav_artists" },
    { to: "/shop", key: "shop", i18nFallback: "nav_shop" },
    { to: "/contact", key: "contact", i18nFallback: "nav_contact" },
];

// Geolocation default — resolve from browser language on first mount (only if
// the user hasn't manually chosen yet).
function inferLangFromLocale() {
    if (typeof navigator === "undefined") return "en";
    const raw = (navigator.language || navigator.userLanguage || "en").toLowerCase();
    // Spanish-speaking locales
    if (raw.startsWith("es")) return "es";
    return "en";
}

export const Header = () => {
    const { lang, setLang, t } = useT();
    const { content, loaded } = useSiteContent();

    // Set default lang from geolocation (only once per session — persists via i18n)
    useEffect(() => {
        try {
            const stored = typeof localStorage !== "undefined" ? localStorage.getItem("itzkatu_lang_touched") : null;
            if (!stored) {
                const inferred = inferLangFromLocale();
                if (inferred !== lang) setLang(inferred);
                if (typeof localStorage !== "undefined") localStorage.setItem("itzkatu_lang_touched", "1");
            }
        } catch (err) { /* ignore */ }
    }, []);

    const visible = CORE_LINKS.filter((l) => {
        const flag = content[`nav_show_${l.key}`];
        if (!loaded || flag == null) return true;
        return flag === "1" || flag === true;
    });
    const labelFor = (l) => {
        const custom = content[`nav_label_${l.key}_${lang}`];
        const raw = (custom && custom.trim()) ? custom.trim() : t(l.i18nFallback);
        return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
    };

    // Optional nav-only scheme override (admin → site → nav → scheme picker).
    // On /after-dark the nav is FORCED to night so it stays consistent with the
    // page's dark aesthetic (users still see the nav to get back out).
    const location = useLocation();
    const isAfterDark = /^\/after-dark(\/|$)/.test(location.pathname);
    const navSchemeKey = isAfterDark ? "night" : (content.section_scheme_nav || "").trim();
    const navScheme = navSchemeKey ? sectionScheme(navSchemeKey) : null;
    // Effective header palette in raw hex — nav override wins, else the page's.
    // Used for the lang toggle so its contrast NEVER depends on CSS vars that
    // may not match the nav's own background (bug: invisible EN/USD text).
    const { scheme: pageSchemeName } = pageSchemeFor(location.pathname, content);
    const hdrHex = navSchemeKey ? schemeHex(navSchemeKey) : schemeHex(pageSchemeName);

    // Admin-driven typography sizes for the nav bar. Keeps tailwind classes
    // pinned so JIT compiler doesn't strip them.
    const NAV_LINK_SIZES = {
        xs:  "text-[10px] tracking-[0.22em]",
        sm:  "text-[11px] tracking-[0.22em]",
        md:  "text-xs tracking-[0.2em]",
        lg:  "text-sm tracking-[0.18em]",
        xl:  "text-base tracking-[0.16em]",
    };
    const NAV_BRAND_SIZES = {
        sm:  "text-lg md:text-xl",
        md:  "text-xl md:text-2xl",
        lg:  "text-2xl md:text-3xl",
        xl:  "text-3xl md:text-4xl",
        xxl: "text-4xl md:text-5xl",
    };
    const navLinkCls = NAV_LINK_SIZES[content.nav_link_size] || NAV_LINK_SIZES.sm;
    const navBrandCls = NAV_BRAND_SIZES[content.nav_brand_size] || NAV_BRAND_SIZES.md;

    return (
        <header
            data-testid="site-header"
            className={`w-full px-4 md:px-8 ${navScheme ? `${navScheme.bg} ${navScheme.text}` : ""}`}
            style={navSchemeKey ? { "--sc-bg": hdrHex.bg, "--sc-text": hdrHex.text, "--sc-accent": hdrHex.accent } : undefined}
        >
            <div className={`max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-y-3 gap-x-4 md:gap-x-8 py-3 md:py-4 ${content.nav_border_bottom === "1" ? "border-b border-current/15" : ""}`}>
                <Link
                    to="/"
                    data-testid="brand-logo"
                    className={`font-heading font-black tracking-tighter text-current ${navBrandCls}`}
                >
                    ITZKATU
                </Link>
                <nav className="flex items-center gap-3 md:gap-6 flex-wrap justify-end">
                {visible.map((l) => (
                    <NavLink
                        key={l.to}
                        to={l.to}
                        data-testid={`nav-${l.key}`}
                        className={({ isActive }) =>
                            `font-mono transition-colors hover:text-[color:var(--sc-accent)] ${navLinkCls} ${isActive ? "text-[color:var(--sc-accent)]" : "text-current opacity-80"}`
                        }
                    >
                        {labelFor(l)}
                    </NavLink>
                ))}
                {/* Language toggle — single boxed container with two inner cells (EN | ES).
                    Active cell fills with the current text color for high contrast. */}
                <div
                    className="inline-flex items-stretch border border-current rounded-sm overflow-hidden"
                    data-testid="lang-toggle"
                >
                    {["en", "es"].map((l, i) => (
                        <button
                            key={l}
                            onClick={() => setLang(l)}
                            data-testid={`lang-${l}`}
                            className={`font-mono text-[11px] uppercase tracking-[0.2em] px-2.5 py-1 transition-colors ${
                                lang === l ? "" : "opacity-70 hover:opacity-100"
                            } ${i === 0 ? "border-r border-current" : ""}`}
                            style={lang === l ? { backgroundColor: hdrHex.text, color: hdrHex.bg } : undefined}
                        >
                            {l} / {l === "en" ? "usd" : "mxn"}
                        </button>
                    ))}
                </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;
